import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { CircleXIcon, ExternalLinkIcon } from '@air/design-icons';
import { FileStatusButton } from '@air/feature-file-status/ui';
import { removeUploadsAction, uploadByIdSelector } from '@air/redux-uploader';

import { getWorkspaceUploadsItemStatusCopy } from '~/components/v2/components/workspace/workspace-uploads/getWorkspaceUploadsItemStatusCopy';
import { useWorkspaceUploadsOpenCompletedAsset } from '~/components/v2/components/workspace/workspace-uploads/useWorkspaceUploadsOpenCompletedAsset';
import { WorkspaceUploadsListItem } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploadsListItem';
import { useAirSelector } from '~/utils/ReduxUtils';

export type WorkspaceUploadsCompletedListItemProps = {
  uploadId: string;
};

export const WorkspaceUploadsCompletedListItem = memo(({ uploadId }: WorkspaceUploadsCompletedListItemProps) => {
  const dispatch = useDispatch();
  const upload = useAirSelector((state) => uploadByIdSelector(state, uploadId));
  const { openCompletedAsset } = useWorkspaceUploadsOpenCompletedAsset();

  const handleRemove = useCallback(() => {
    dispatch(removeUploadsAction({ uploadIds: [uploadId] }));
  }, [dispatch, uploadId]);

  const handleOpenAsset = useCallback(() => {
    if (!upload?.clipId) {
      return;
    }

    openCompletedAsset(upload.clipId);
  }, [openCompletedAsset, upload?.clipId]);

  if (!upload) {
    return null;
  }

  const sizeInBytes = upload.apiUploadData.size || upload.file.size || 0;
  const statusCopy = getWorkspaceUploadsItemStatusCopy({
    currentBytesUploaded: sizeInBytes,
    sizeInBytes,
    status: upload.status,
  });

  return (
    <WorkspaceUploadsListItem
      actions={
        <>
          {upload.clipId ? (
            <FileStatusButton
              appearance="ghost"
              data-testid="WORKSPACE_UPLOADS_ITEM_OPEN_ASSET"
              label="Open asset"
              onClick={handleOpenAsset}
            >
              <ExternalLinkIcon aria-hidden size="sm" />
            </FileStatusButton>
          ) : null}
          <FileStatusButton
            appearance="ghost"
            data-testid="WORKSPACE_UPLOADS_ITEM_REMOVE"
            label="Remove"
            onClick={handleRemove}
          >
            <CircleXIcon aria-hidden size="sm" />
          </FileStatusButton>
        </>
      }
      dimmed
      ext={upload.apiUploadData.ext ?? ''}
      statusCopy={statusCopy}
      thumbnailSource={upload.imageBase64}
      title={upload.apiUploadData.importedName ?? ''}
      uploadId={upload.id}
    />
  );
});

WorkspaceUploadsCompletedListItem.displayName = 'WorkspaceUploadsCompletedListItem';
